// Import a programme someone already has (a coach's spreadsheet, a PDF read
// by AI, or Khronos's own template) as a fixed programme: every week's
// sessions written out in full, run as written.
//
// Three stages, all pure so they can be unit-tested:
//   1. sheet rows → import rows: the template's columns, or a coach's table
//      read by its header (readSheetRows). AI reads anything else into the
//      same import rows (apis/llm/operations.js readProgrammeText).
//   2. import rows → weeks of sessions (groupSessions), each session a plan
//      tree built here, never by a model (buildSessionPlan).
//   3. weeks → the programme the API stores (importedProgrammePayload).
//
// An import row is one exercise as written:
//   { week, day, session, part, exercise, sets, amount, load, target, rpe,
//     tempo, rest, notes, role }
// `day` is 0 (Monday) to 6 (Sunday) or null; the rest are text as written.

import { genStableId } from "./planEditing";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_RE = /^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\b/i;

/** Monday 0 to Sunday 6 from "Tuesday", "TUE", "3" (1-based) or a number. */
export const parseDay = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const named = text.match(WEEKDAY_RE);
  if (named) return WEEKDAYS.indexOf(named[1].toLowerCase());
  const n = Number(text.match(/^\d+/)?.[0]);
  return Number.isInteger(n) && n >= 1 && n <= 7 ? n - 1 : null;
};

/** Week numbers from "3", "1-4", "1–4", "1, 3, 5" or "Week 2". */
export const parseWeeks = (value) => {
  const text = String(value ?? "").replace(/week|wk|w/gi, "").trim();
  if (!text) return [];
  const weeks = [];
  for (const part of text.split(/[,;&]|\band\b/)) {
    const range = part.match(/(\d+)\s*(?:-|–|—|to)\s*(\d+)/);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])].sort((x, y) => x - y);
      for (let w = a; w <= Math.min(b, a + 52); w++) weeks.push(w);
    } else {
      const n = Number(part.match(/\d+/)?.[0]);
      if (Number.isInteger(n) && n > 0) weeks.push(n);
    }
  }
  return [...new Set(weeks)].filter((w) => w <= 104);
};

// ── Amounts ────────────────────────────────────────────────────────────────

const num = (s) => Number(String(s).replace(",", "."));

const SECONDS = { s: 1, sec: 1, secs: 1, second: 1, seconds: 1, min: 60, mins: 60, minute: 60, minutes: 60, h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600 };

/** Seconds from "90 s", "2 min", "1:30", "2-3 min" (the lower end); null when no time. */
export const parseSeconds = (value) => {
  const text = String(value ?? "").toLowerCase().trim();
  if (!text) return null;
  const clock = text.match(/^(\d+):(\d{2})$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const m = text.match(/(\d+(?:[.,]\d+)?)(?:\s*(?:-|–|to)\s*\d+(?:[.,]\d+)?)?\s*(seconds?|secs?|s|minutes?|mins?|min|hours?|hrs?|hr|h)\b/);
  if (!m) return null;
  return Math.round(num(m[1]) * SECONDS[m[2]]);
};

/** Metres from "30 m", "500m", "2 km", "2k"; null when no distance. */
export const parseMetres = (value) => {
  const text = String(value ?? "").toLowerCase();
  const km = text.match(/(\d+(?:[.,]\d+)?)\s*(?:km|k)\b/);
  if (km) return Math.round(num(km[1]) * 1000);
  const m = text.match(/(\d+(?:[.,]\d+)?)\s*(?:m|metres?|meters?)\b/);
  return m ? Math.round(num(m[1])) : null;
};

const EACH_SIDE_RE = /\b(each|per|a)\s+(side|leg|arm|way|direction)\b|\/\s*side\b|\bL\s*\+\s*R\b/i;

/**
 * What one set asks for, from the "Reps / time" text as written:
 *   { unit: "reps"|"cal"|"s", value, each, label, perSet? }
 * `each` marks "each side"; `perSet` holds a ramp ("5 / 3 / 2"); `label`
 * carries a distance ("30 m") or anything else to show beside the name. Null
 * for nothing, or text with no amount in it ("Club programmed").
 */
export const parseAmount = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const each = EACH_SIDE_RE.test(raw);
  const text = raw.replace(EACH_SIDE_RE, "").trim();

  // A ramp, one entry per set: "5 / 3 / 2" or "8-6-4" reps.
  const ramp = text.match(/^(\d+)(?:\s*[/,]\s*\d+){1,9}\s*(reps?)?$/i);
  if (ramp) {
    const perSet = text.match(/\d+/g).map(Number);
    return { unit: "reps", value: perSet[0], perSet, each, label: null };
  }

  // "40 s on / 90 s easy": the work is the first part; the rest is read by
  // parseRestFromAmount.
  const onOff = text.match(/^(.+?)\s+on\b/i);
  const work = onOff ? onOff[1] : text;

  const cal = work.match(/(\d+)\s*(?:cal|cals|calories|kcal)\b/i);
  if (cal) return { unit: "cal", value: Number(cal[1]), each, label: null };

  const secs = parseSeconds(work);
  // "3 × 5 s": count of holds, not one long hold.
  const holds = work.match(/^(\d+)\s*[x×]\s*(.+)$/i);
  if (holds && parseSeconds(holds[2])) {
    return { unit: "reps", value: Number(holds[1]), each, label: `${holds[2].trim()} holds` };
  }
  if (secs) return { unit: "s", value: secs, each, label: null };

  const metres = parseMetres(work);
  if (metres) return { unit: "reps", value: 1, each, label: formatDistance(metres) };

  const reps = work.match(/^(\d+)(?:\s*(?:-|–|to)\s*(\d+))?\s*(?:reps?|x)?\b(.*)$/i);
  if (reps) {
    const rest = reps[3].trim();
    return {
      unit: "reps",
      value: Number(reps[1]),
      each,
      label: reps[2] ? `${reps[1]}-${reps[2]} reps` : rest || null,
    };
  }
  return null;
};

const formatDistance = (m) =>
  m >= 1000 ? `${Number((m / 1000).toFixed(2))} km` : `${m} m`;

/** The easy part of "40 s on / 90 s easy", in seconds. */
export const parseRestFromAmount = (value) => {
  const m = String(value ?? "").match(/\bon\s*\/\s*(.+)$/i);
  return m ? parseSeconds(m[1]) : null;
};

/** Rest between sets in seconds: "90 s", "2-3 min", "60-90 s"; null for "Continuous" or "-". */
export const parseRest = (value) => parseSeconds(value);

/**
 * A load as written: kg (one figure, a range, or one per set) and the cue
 * to show. "47.5-50 kg" is the lower end; "20 / 27.5 / 37.5 kg" a ramp.
 */
export const parseLoad = (target, load) => {
  const text = String(target || "").trim();
  const kgText = /kg\b/i.test(text) ? text : /kg\b/i.test(String(load || "")) ? String(load) : "";
  let weight = null;
  let perSet = null;
  if (kgText) {
    const figures = (kgText.match(/\d+(?:[.,]\d+)?/g) || []).map(num);
    if (/\//.test(kgText) && figures.length > 1) perSet = figures;
    else if (figures.length) weight = figures[0];
  }
  const pct = String(load || "").match(/(\d+(?:\.\d+)?)\s*(?:-\s*\d+(?:\.\d+)?)?\s*%/);
  return { weight, perSet, intensityPct: pct && !kgText ? Number(pct[1]) : null };
};

// ── Sheets → import rows ───────────────────────────────────────────────────

// Columns a coach's sheet names, by what they hold. The first that matches a
// header cell wins; columns the athlete fills in (done, kg used, my notes)
// match nothing and are left behind.
const COLUMNS = [
  ["week", /^(week|wk)s?$/i],
  ["day", /^day$/i],
  ["session", /^session( name)?$/i],
  ["part", /^(part|block|section|phase|order)$/i],
  ["exercise", /^(exercise|movement|exercise name|drill)s?$/i],
  ["sets", /^(sets|rounds)$/i],
  ["amount", /^(reps?( \/ time)?|reps or time|time|duration|distance|reps \/ distance|work)$/i],
  ["load", /^(load( \/ intensity)?|intensity|weight|%|% ?1rm)$/i],
  ["target", /^(your target|target( load)?|target kg|kg)$/i],
  ["rpe", /^(target )?rpe$/i],
  ["tempo", /^(tempo|tempo \/ rate|rate)$/i],
  ["rest", /^rest( between sets)?( \(s\))?$/i],
  ["notes", /^(notes|coach cue|cue|coaching notes|comments)$/i],
];

const ATHLETE_COLUMN_RE = /^(my|done|kg used|completed|actual|logged)\b/i;

/** Map a header row to { field: columnIndex }; null when it isn't a header. */
export const headerColumns = (row) => {
  const map = {};
  (row || []).forEach((cell, i) => {
    const text = String(cell || "").replace(/\s+/g, " ").trim();
    if (!text || ATHLETE_COLUMN_RE.test(text)) return;
    for (const [field, re] of COLUMNS) {
      if (map[field] == null && re.test(text)) {
        map[field] = i;
        break;
      }
    }
  });
  return map.exercise != null && (map.sets != null || map.amount != null) ? map : null;
};

const cell = (row, map, field) => (map[field] == null ? "" : String(row[map[field]] ?? "").trim());

/** The week a sheet holds, from its name ("B1 W3 PROGRAM", "Week 3") or title rows. */
export const sheetWeek = (name, titleRows = []) => {
  const fromName = String(name || "").match(/\bw(?:ee)?k?\s*(\d{1,2})\b/i);
  if (fromName) return Number(fromName[1]);
  for (const row of titleRows) {
    const m = String((row || []).join(" ")).match(/\bweek\s*(\d{1,2})\b/i);
    if (m) return Number(m[1]);
  }
  return null;
};

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** The first date a sheet's title rows give ("Mon 5 Oct - Sun 11 Oct 2026"), as YYYY-MM-DD. */
export const titleDate = (titleRows = []) => {
  for (const row of titleRows) {
    const text = String((row || []).join(" "));
    const d = text.match(/\b(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i);
    const y = text.match(/\b(20\d{2})\b/);
    if (d && y) {
      const month = MONTHS.indexOf(d[2].toLowerCase());
      const date = new Date(Date.UTC(Number(y[1]), month, Number(d[1])));
      if (date.getUTCMonth() === month) return date.toISOString().slice(0, 10);
    }
  }
  return null;
};

const ROLE_RE = /^(sweep|drummer|steer|cox|goalie|keeper)s?\b/i;
const SESSION_RE = /^(AM\/PM|AM|PM|HUB|SESSION|WORKOUT|DAY \d+)\b/i;
const OWN_SESSION_RE = /^(AM\/PM|AM|PM)\s*[·:-]/i;

/**
 * Read one sheet into import rows by its header: the Khronos template, or a
 * coach's table with day rows ("TUESDAY · 6 Oct") and session rows
 * ("AM · BUILD - GYM A · ≤ 65 min") between the exercises.
 * @returns {{ rows: object[], week: number|null, columns: object }|null}
 *   null when no header was found (AI reads it instead).
 */
export const readSheetRows = (sheet) => {
  const all = sheet?.rows || [];
  const at = all.slice(0, 40).findIndex((r) => headerColumns(r));
  if (at < 0) return null;
  const map = headerColumns(all[at]);
  const week = sheetWeek(sheet.name, all.slice(0, at));
  const date = titleDate(all.slice(0, at));
  const rows = [];
  let day = null;
  let session = null;
  let goal = null;

  for (const row of all.slice(at + 1)) {
    const filled = row.filter(Boolean);
    if (!filled.length) continue;
    const first = String(filled[0]);
    if (/summary/i.test(first)) break;
    // A second header (a summary table under the week) ends the week.
    if (headerColumns(row) && rows.length) break;

    const exercise = cell(row, map, "exercise");
    const sets = cell(row, map, "sets");
    const amount = cell(row, map, "amount");
    const part = cell(row, map, "part");

    // A day row: a weekday name and little else.
    // ("SAT 10 OR SUN 11 OCT · HUB 1" is a session you place yourself.)
    if (WEEKDAY_RE.test(first) && !/\bor\b/i.test(first) && filled.length <= 2 && !sets && !amount) {
      day = parseDay(first);
      session = null;
      continue;
    }
    // A session heading: no sets or amount, named in the first cell.
    if (!sets && !amount && (SESSION_RE.test(first) || filled.length <= 3)) {
      session = first;
      goal = filled.slice(1).find((c) => /^goal\b/i.test(c)) || null;
      continue;
    }
    if (!exercise && !amount) continue;

    // A one-row session ("PM · CLUB", "PM · RESET") names itself in its part.
    const ownSession = OWN_SESSION_RE.test(part);
    rows.push({
      week: cell(row, map, "week") || week,
      day: map.day != null ? parseDay(cell(row, map, "day")) : day,
      // A row before any heading ("REST DAY") is named by its part.
      session: map.session != null ? cell(row, map, "session") : ownSession ? part : session ?? (part || null),
      part: ownSession ? "" : part,
      exercise,
      sets,
      amount,
      load: cell(row, map, "load"),
      target: cell(row, map, "target"),
      rpe: cell(row, map, "rpe"),
      tempo: cell(row, map, "tempo"),
      rest: cell(row, map, "rest"),
      notes: cell(row, map, "notes"),
      role: ROLE_RE.test(part) ? part.match(ROLE_RE)[1].toLowerCase() : null,
      goal: ownSession ? null : goal,
    });
  }
  return { rows, week, date, columns: map };
};

/** "AM  ·  BUILD - GYM A  ·  ≤ 65 min" → "Build - Gym A". */
export const cleanSessionName = (text) => {
  const parts = String(text || "")
    .split(/\s+·\s+|\s+\|\s+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !/^(AM\/PM|AM|PM)$/i.test(p) && !/^[≤<]?\s*\d+\s*min$/i.test(p) && !/your choice/i.test(p));
  const name = (parts[0] || String(text || "")).replace(/^(AM\/PM|AM|PM)\s*[·:-]?\s*/i, "").trim();
  // A heading in capitals ("BUILD - GYM A") reads better in title case.
  const letters = name.replace(/[^a-z]/gi, "");
  const capitals = letters.replace(/[^A-Z]/g, "").length;
  const titled =
    letters && capitals / letters.length > 0.7
      ? name.toLowerCase().replace(/(^|[\s(/-])([a-z])/g, (m, a, c) => a + c.toUpperCase())
      : name;
  return titled.replace(/(\d)\s+Min\b/g, "$1 min").slice(0, 120) || "Session";
};

/** The cap a session heading states ("≤ 65 min"), in minutes. */
export const sessionCapMinutes = (text) => {
  const m = String(text || "").match(/(?:[≤<]|\bcap(?:ped)?(?: at)?|\bmax)\s*(\d+)\s*min\b/i);
  return m ? Number(m[1]) : null;
};

// ── Import rows → sessions ────────────────────────────────────────────────

/**
 * Keep the rows for one role. Rows marked for a role (a sweep or drummer
 * block) are kept only for that role; when a session has them and their
 * notes say they replace a part ("instead of the P3 exercises"), that part
 * goes. With no role picked, role rows go.
 */
export const filterForRole = (rows, role) => {
  const want = role ? String(role).toLowerCase() : null;
  const bySession = new Map();
  for (const row of rows) {
    const key = `${row.week}|${row.day}|${row.session}`;
    if (!bySession.has(key)) bySession.set(key, []);
    bySession.get(key).push(row);
  }
  const out = [];
  for (const list of bySession.values()) {
    const mine = list.filter((r) => r.role && r.role === want);
    const replaced = new Set(
      mine.flatMap((r) => {
        const m = `${r.notes} ${r.exercise}`.match(/instead of (?:the )?(P\d)\b/i);
        return m ? [m[1].toUpperCase()] : [];
      }),
    );
    for (const r of list) {
      if (r.role && r.role !== want) continue;
      const p = String(r.part || "").match(/^P\d/i)?.[0].toUpperCase();
      if (!r.role && p && replaced.has(p)) continue;
      out.push(r);
    }
  }
  return out;
};

/** The roles a set of rows has blocks for ("sweep", "drummer"). */
export const rolesIn = (rows) => [...new Set(rows.map((r) => r.role).filter(Boolean))];

const intensityCue = (row) =>
  [
    row.load && !/^bodyweight$/i.test(row.load) ? row.load : null,
    row.rpe && !/rpe/i.test(row.load || "") ? `RPE ${row.rpe}` : null,
    row.tempo && !/^[-–]$/.test(row.tempo) ? row.tempo : null,
  ]
    .filter(Boolean)
    .join(" · ") || null;

const isWarmup = (part) => /warm|prep|release|mobilis|activat|prime|ramp/i.test(part || "");
const isCooldown = (part) => /cool|stretch/i.test(part || "");

/** One exercise as plan nodes: a sets group, or a single step, plus its rests. */
export const exerciseNodes = (row) => {
  const amount = parseAmount(row.amount);
  const sets = Math.max(1, Math.min(Number(String(row.sets).match(/\d+/)?.[0]) || 1, 20));
  const name = String(row.exercise || row.part || "Exercise").replace(/\s+/g, " ").trim().slice(0, 140);
  const load = parseLoad(row.target, row.load);
  const rest = parseRest(row.rest) ?? parseRestFromAmount(row.amount);

  let unit = amount?.unit || "reps";
  let value = amount?.value || 1;
  // A timed step done each side runs as one clock for both sides.
  if (amount?.each && unit === "s") value *= 2;
  const extras = [amount?.label, amount?.each ? "each side" : null].filter(Boolean);
  if (!amount && row.amount) extras.push(row.amount);
  const label = extras.length ? `${name} (${extras.join(", ")})` : name;

  const work = {
    id: genStableId(),
    type: "Work",
    label,
    value,
    unit,
    autoStart: unit === "s",
  };
  const cue = intensityCue(row);
  if (cue) work.intensityTarget = cue;
  if (load.weight) work.weight = load.weight;
  if (load.intensityPct) work.intensityPct = load.intensityPct;
  if (row.notes) work.notes = String(row.notes).slice(0, 300);
  const scheme = {};
  if (amount?.perSet?.length === sets) scheme.value = amount.perSet;
  if (load.perSet?.length === sets) scheme.weight = load.perSet;
  if (Object.keys(scheme).length) work.setScheme = scheme;

  const restNode = rest
    ? { id: genStableId(), type: "Rest", label: "Rest", unit: "s", value: rest, autoStart: true }
    : null;
  if (sets === 1) return [work];
  return [
    {
      id: genStableId(),
      type: "Group",
      label: name,
      unit: "sets",
      value: sets,
      children: restNode ? [work, restNode] : [work],
    },
  ];
};

const SIDE_RE = /^(left|right|l|r)\b/i;

/**
 * "LEFT reps 1, 3: 6 min" then "RIGHT reps 2, 4: 6 min": the sides take
 * turns, so they run as one block of sets, left then right in each.
 */
const sidePair = (a, b) => {
  if (!a || !b) return null;
  const sa = String(a.exercise).match(SIDE_RE)?.[1]?.[0].toLowerCase();
  const sb = String(b.exercise).match(SIDE_RE)?.[1]?.[0].toLowerCase();
  if (!sa || !sb || sa === sb || a.sets !== b.sets || a.amount !== b.amount || a.part !== b.part) return null;
  const sets = Number(String(a.sets).match(/\d+/)?.[0]) || 1;
  const steps = [a, b].flatMap((row) => {
    const [node] = exerciseNodes({ ...row, sets: "1" });
    const rest = parseRest(row.rest) ?? parseRestFromAmount(row.amount);
    return rest
      ? [node, { id: genStableId(), type: "Rest", label: "Rest", unit: "s", value: rest, autoStart: true }]
      : [node];
  });
  return { id: genStableId(), type: "Group", label: `${a.part || "Main"}: left and right`, unit: "sets", value: sets, children: steps };
};

const blockOf = (part) => (isWarmup(part) ? "Warm-up" : isCooldown(part) ? "Cool-down" : "Main");

/**
 * A session's plan tree from its rows, in the order written: warm-up rows in
 * a Warm-up block, cool-down rows in a Cool-down block, the rest in Main.
 */
export const buildSessionPlan = (name, rows, { goal } = {}) => {
  const blocks = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const label = blockOf(row.part);
    let block = blocks[blocks.length - 1];
    if (!block || block.label !== label) {
      block = { id: genStableId(), type: "Group", label, unit: "rounds", value: 1, children: [] };
      blocks.push(block);
    }
    const pair = sidePair(row, rows[i + 1]);
    if (pair) {
      block.children.push(pair);
      i++;
    } else {
      block.children.push(...exerciseNodes(row));
    }
  }
  const plan = {
    id: genStableId(),
    type: "Group",
    label: name,
    unit: "rounds",
    value: 1,
    children: blocks.length === 1 && blocks[0].label === "Main" ? blocks[0].children : blocks,
    meta: { source: "import" },
  };
  if (goal) plan.meta.historyNote = String(goal).replace(/^goal:?\s*/i, "").slice(0, 300);
  return plan;
};

// Day kinds (config/periodisation.js DAY_KINDS) a session's name suggests.
const KIND_RULES = [
  [/threshold|tempo|interval|power|sprint|speed|hiit|vo2/i, "intervals"],
  [/erg|row|bike|run|swim|paddle|ride|engine|aerobic|zone ?2|z2|easy|steady/i, "zone2"],
  [/hub|circuit|metcon|conditioning|crossfit/i, "resistance_intervals"],
  [/gym|strength|build|perform|lift|weights|upper|lower|push|pull|legs|squat|deadlift|press/i, "resistance"],
];

/** The day kind a session's name and rows suggest; null when nothing fits. */
export const guessDayKind = (name, rows = []) => {
  for (const [re, kind] of KIND_RULES) if (re.test(name)) return kind;
  const text = rows.map((r) => r.exercise).join(" ");
  for (const [re, kind] of KIND_RULES) if (re.test(text)) return kind;
  return null;
};

const EXTRA_RE = /\b(club|water|regatta|reset|recovery|rest day|check-?in|yoga|mobility)\b/i;

/**
 * Weeks of sessions from import rows.
 * @returns {{ week: number, sessions: { key, week, day, name, rows, extra, kind, capMinutes }[] }[]}
 *   `extra` marks sessions shown as a note on the day rather than run as a
 *   programme session: one-row sessions (club water, a mobility reminder)
 *   unless they're long (a 90-minute HUB).
 */
export const groupSessions = (rows) => {
  const weeks = new Map();
  for (const row of rows) {
    for (const week of parseWeeks(row.week)) {
      if (!weeks.has(week)) weeks.set(week, new Map());
      const sessions = weeks.get(week);
      const name = String(row.session || "").trim() || (row.day != null ? `${DAY_NAMES[row.day]} session` : "Session");
      const key = `${row.day ?? "-"}|${name}`;
      if (!sessions.has(key)) sessions.set(key, { key, week, day: row.day ?? null, name: cleanSessionName(name), rawName: name, rows: [] });
      sessions.get(key).rows.push(row);
    }
  }
  return [...weeks.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, sessions]) => ({
      week,
      sessions: [...sessions.values()].map((s) => {
        const real = s.rows.filter((r) => !/check-?in/i.test(`${r.part} ${r.exercise}`));
        const long = real.length === 1 && (parseSeconds(real[0].amount) || 0) >= 60 * 60;
        const extra = real.length <= 1 && !long && (EXTRA_RE.test(`${s.rawName} ${real[0]?.exercise || ""}`) || !parseAmount(real[0]?.amount));
        return {
          ...s,
          extra,
          kind: guessDayKind(s.name, s.rows),
          capMinutes: sessionCapMinutes(s.rawName),
        };
      }),
    }));
};

/** A one-line note for an extra ("PM club water"), for the day it sits on. */
export const extraNote = (session) => {
  const row = session.rows[0] || {};
  const what = String(row.exercise || "").split(/\s+[-–]\s+|\s+·\s+/)[0].trim();
  const amount = row.amount && parseAmount(row.amount) ? ` (${row.amount})` : "";
  const name = session.name.toLowerCase();
  const text = !what ? session.name : what.toLowerCase().includes(name) ? what : name.includes(what.toLowerCase()) ? session.name : `${session.name}: ${what}`;
  return `${text}${amount}`.slice(0, 160);
};

// ── Sessions → the programme the API stores ───────────────────────────────

const mondayOf = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
};

const isoDate = (d) => d.toISOString().slice(0, 10);

/**
 * The programme to create from reviewed weeks.
 * @param {object} opts
 * @param {string} opts.title
 * @param {ReturnType<typeof groupSessions>} opts.weeks  as reviewed (extras marked)
 * @param {number} opts.startWeek  the week the athlete is up to (earlier weeks are skipped)
 * @param {string} opts.weekOneMonday  YYYY-MM-DD, the Monday week 1 began (or begins)
 * @param {object} [opts.source]  { file, author } for the programme's credit
 */
export const importedProgrammePayload = ({ title, weeks, startWeek = 1, weekOneMonday, source = {} }) => {
  const sorted = [...weeks].sort((a, b) => a.week - b.week);
  const first = sorted[0]?.week || 1;
  const last = sorted[sorted.length - 1]?.week || 1;
  const duration = last - first + 1;
  const mains = (w) => w.sessions.filter((s) => !s.extra);
  const perWeek = Math.max(1, ...sorted.map((w) => mains(w).length));

  // Training days from the first full week, when each session has its own day.
  const model = sorted.find((w) => mains(w).length === perWeek);
  const modelDays = model ? mains(model).map((s) => s.day) : [];
  const weekdays =
    perWeek <= 7 && modelDays.every((d) => d != null) && new Set(modelDays).size === perWeek
      ? [...modelDays].sort((a, b) => a - b)
      : null;

  const slots = [];
  for (let week = first; week <= last; week++) {
    const w = sorted.find((x) => x.week === week) || { week, sessions: [] };
    const sessions = mains(w).sort((a, b) => (a.day ?? 9) - (b.day ?? 9));
    const extras = w.sessions.filter((s) => s.extra);
    for (let i = 0; i < perWeek; i++) {
      const s = sessions[i];
      const number = week - first + 1;
      if (!s) {
        slots.push({
          week_number: number,
          day_index: i,
          scheduled_label: "No session this week",
          day_kind: null,
          status: "skipped",
        });
        continue;
      }
      const sameDay = extras.filter((e) => e.day != null && e.day === s.day);
      const note = sameDay.length ? `Also today: ${sameDay.map(extraNote).join("; ")}` : null;
      const plan = buildSessionPlan(s.name, filterOut(s.rows), { goal: s.rows.find((r) => r.goal)?.goal });
      slots.push({
        week_number: number,
        day_index: i,
        scheduled_label: s.name.slice(0, 255),
        day_kind: s.kind,
        target_minutes: s.capMinutes || null,
        plan_note: note,
        plan_data: plan,
        planned_weekday: weekdays && s.day != null && s.day !== weekdays[i] ? s.day : null,
        status: week < startWeek ? "skipped" : "pending",
      });
    }
  }

  return {
    title: String(title || "Imported programme").slice(0, 255),
    source: "imported",
    source_meta: { file: source.file || null, author: source.author || null, first_week: first },
    duration_weeks: duration,
    sessions_per_week: perWeek,
    weekdays,
    first_day: isoDate(mondayOf(weekOneMonday)),
    split: null,
    progression_style: "fixed",
    focus: null,
    slots,
  };
};

/**
 * Where to start, by default: the week the sheet's own dates put today in
 * (a Sunday looks ahead to the week starting tomorrow), else week 1 from
 * this coming Monday.
 * @param {{ week: number, date: string|null }[]} dated  each sheet's week and first date
 * @param {string} today  YYYY-MM-DD
 * @returns {{ startWeek: number, weekOneMonday: string }}
 */
export const defaultStart = (dated, today) => {
  const day = mondayOf(today);
  if (new Date(`${today}T00:00:00Z`).getUTCDay() === 0) day.setUTCDate(day.getUTCDate() + 7);
  const known = (dated || []).find((d) => d.date && d.week);
  if (known) {
    const one = mondayOf(known.date);
    one.setUTCDate(one.getUTCDate() - 7 * (known.week - 1));
    const week = Math.floor((day - one) / (7 * 86400000)) + 1;
    return { startWeek: Math.max(1, week), weekOneMonday: isoDate(one) };
  }
  const next = mondayOf(today);
  if (new Date(`${today}T00:00:00Z`).getUTCDay() !== 1) next.setUTCDate(next.getUTCDate() + 7);
  return { startWeek: 1, weekOneMonday: isoDate(next) };
};

// Check-in rows only remind the athlete to talk to the coach.
const filterOut = (rows) => rows.filter((r) => !/check-?in/i.test(`${r.part} ${r.exercise}`));

// ── The Khronos template ──────────────────────────────────────────────────

export const TEMPLATE_HEADER = ["Week", "Day", "Session", "Part", "Exercise", "Sets", "Reps / time", "Load", "RPE", "Rest", "Notes"];

/** The downloadable template, as CSV, with a few example rows. */
export const templateCsv = () =>
  [
    TEMPLATE_HEADER,
    ["1-4", "Tue", "Gym A", "Warm-up", "Glute bridge", "1", "10", "", "", "20 s", ""],
    ["1-4", "Tue", "Gym A", "Main", "Back squat", "4", "6", "60 kg", "7", "2 min", "Tempo 3-1-1"],
    ["1-4", "Tue", "Gym A", "Main", "Suitcase carry", "3", "30 m each side", "20 kg", "6-7", "60 s", ""],
    ["1-4", "Thu", "Erg intervals", "Main", "Row hard", "8", "40 s", "", "8", "90 s", "Easy paddle between"],
    ["5-8", "Tue", "Gym A", "Main", "Back squat", "4", "5", "65 kg", "8", "2 min", ""],
  ]
    .map((r) => r.map((c) => (/[,"]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(","))
    .join("\n");
